import { ResponsiveTokens, Theme } from '../../theme/index.types';
import { ExtendableStyles, Styles } from '../../types/styles';

import { Transformers } from './index.types';
import { STATIC_TOKENS } from '../../theme/index.content';
import { Breakpoint } from '../useBreakpoint/index.types';
import { isBreakpointObject } from '../../utils';

const SPACING_PROPERTIES = [
  'padding',
  'paddingBlock',
  'paddingBlockEnd',
  'paddingBlockStart',
  'paddingBottom',
  'paddingEnd',
  'paddingHorizontal',
  'paddingInline',
  'paddingInlineEnd',
  'paddingInlineStart',
  'paddingLeft',
  'paddingRight',
  'paddingStart',
  'paddingTop',
  'paddingVertical',
  'margin',
  'marginBlock',
  'marginBlockEnd',
  'marginBlockStart',
  'marginBottom',
  'marginEnd',
  'marginHorizontal',
  'marginInline',
  'marginInlineEnd',
  'marginInlineStart',
  'marginLeft',
  'marginRight',
  'marginStart',
  'marginTop',
  'marginVertical',
  'gap',
  'top',
  'right',
  'bottom',
  'left',
  'width',
  'maxWidth',
  'height',
  'maxHeight',
];

const BORDER_RADIUS_PROPERTIES = [
  'borderRadius',
  'borderEndEndRadius',
  'borderTopEndRadius',
  'borderTopLeftRadius',
  'borderEndStartRadius',
  'borderTopRightRadius',
  'borderStartEndRadius',
  'borderTopStartRadius',
  'borderBottomEndRadius',
  'borderBottomLeftRadius',
  'borderStartStartRadius',
  'borderBottomRightRadius',
  'borderBottomStartRadius',
];

const addResponsiveCSSTransformers = <T extends string[]>(properties: T) =>
  properties.reduce(
    (accumulator, key) => ({
      ...accumulator,
      [key]: ({ value }: { value: string }) => `var(--${value})`,
    }),
    {},
  );

const addResponsiveThemeTransformers = <T extends string[]>(
  properties: T,
  responsiveToken: keyof ResponsiveTokens,
) =>
  properties.reduce(
    (accumulator, key) => ({
      ...accumulator,
      [key]: ({ value, theme }: { value: never; theme: Theme }) =>
        theme[responsiveToken][value],
    }),
    {},
  );

const STATIC_TRANSFORMERS: Transformers = {
  borderWidth: ({ value }) => STATIC_TOKENS.borderWidths[value],
  borderTopWidth: ({ value }) => STATIC_TOKENS.borderWidths[value],
  borderLeftWidth: ({ value }) => STATIC_TOKENS.borderWidths[value],
  borderRightWidth: ({ value }) => STATIC_TOKENS.borderWidths[value],
  borderBottomWidth: ({ value }) => STATIC_TOKENS.borderWidths[value],
  letterSpacing: ({ value }) => STATIC_TOKENS.letterSpacings[value],
  lineHeight: ({ value }) => STATIC_TOKENS.lineHeights[value],
};

const CSS_TRANSFORMERS: Transformers = {
  ...STATIC_TRANSFORMERS,
  ...addResponsiveCSSTransformers(SPACING_PROPERTIES),
  ...addResponsiveCSSTransformers(BORDER_RADIUS_PROPERTIES),

  color: ({ value }) => `var(--${value})`,
  backgroundColor: ({ value }) => `var(--${value})`,
  borderColor: ({ value }) => `var(--${value})`,
  shadowColor: ({ value }) => `var(--${value})`,
  fontSize: ({ value }) => `var(--${value})`,
  boxShadow: ({ value }) =>
    typeof value === 'string'
      ? value
      : value.map(({ color, ...rest }) => ({
          ...rest,
          color: color && `var(--${color})`,
        })),
};

const THEME_TRANSFORMERS: Transformers<{ theme: Theme }> = {
  ...STATIC_TRANSFORMERS,
  ...addResponsiveThemeTransformers(SPACING_PROPERTIES, 'spacing'),
  ...addResponsiveThemeTransformers(BORDER_RADIUS_PROPERTIES, 'borderRadiuses'),
  color: ({ value, theme }) => theme.color[value],
  backgroundColor: ({ value, theme }) => theme.color[value],
  borderColor: ({ value, theme }) => theme.color[value],
  shadowColor: ({ value, theme }) => theme.color[value],
  fontSize: ({ value, theme }) => theme.fontSizes[value],
  width: ({ value, theme }) => theme.spacing[value as never] ?? value,
  maxWidth: ({ value, theme }) => theme.spacing[value as never] ?? value,
  height: ({ value, theme }) => theme.spacing[value as never] ?? value,
  maxHeight: ({ value, theme }) => theme.spacing[value as never] ?? value,
  lineHeight: ({ value, theme, styles }) =>
    styles.fontSize &&
    (Number(STATIC_TOKENS.lineHeights[value].slice(0, -1)) / 100) *
      theme.fontSizes[styles.fontSize],
  gap: ({ value, theme }) => theme.spacing[value],
  boxShadow: ({ value, theme }) =>
    typeof value === 'string'
      ? value
      : value.map(({ color, ...rest }) => ({
          ...rest,
          color: color && theme.color[color],
        })),
};

export const parseStyles = <T extends ExtendableStyles>(
  styles: Styles<T>,
  breakpoint: Breakpoint,
  theme?: Theme,
): T => {
  const transformers = theme ? THEME_TRANSFORMERS : CSS_TRANSFORMERS;
  const parsedStyles: Partial<Record<keyof typeof styles, unknown>> = {};

  Object.keys(styles).forEach((_key) => {
    const key = _key as keyof typeof styles;
    const value = isBreakpointObject(styles[key] as never)
      ? styles[key]?.[breakpoint]
      : styles[key];
    const transform = transformers[key as keyof Transformers];

    parsedStyles[key] =
      transform && value !== undefined
        ? transform({ value, theme, styles } as never)
        : value;
  });

  return parsedStyles as T;
};

export const checkForStyleChanges = <T extends ExtendableStyles>(
  styles: Styles<T>,
  cachedStyles: Styles<T>,
) => {
  if (styles === cachedStyles) {
    return false;
  }

  let hasChanged =
    Object.keys(styles || {}).length !== Object.keys(cachedStyles || {}).length;

  for (const _key in styles) {
    const key = _key as keyof Styles<T>;

    if (styles[key] !== cachedStyles?.[key]) {
      hasChanged = true;

      break;
    }
  }

  return hasChanged;
};
